import React, { useState, useEffect } from 'react';
import {
  FaHome,
  FaPrint,
  FaUserAlt,
  FaCog,
  FaSignOutAlt,
  FaTasks,
  FaUsers,
  FaChartLine,
  FaMoneyBillWave,
  FaPiggyBank,
  FaBoxOpen,
  FaServer,
  FaClipboardList,
  FaFileAlt,
  FaExternalLinkAlt,
  FaFileInvoiceDollar,
  FaStamp,
  FaFileSignature,
  FaCar,
  FaCalendarAlt,
  FaExchangeAlt,
} from 'react-icons/fa';
import { IoMdBusiness } from 'react-icons/io';
import { MdRequestPage } from 'react-icons/md';
import { GiTakeMyMoney } from 'react-icons/gi';
import { useNavigate, Link } from 'react-router-dom';
import { decodeToken, isExpired } from 'react-jwt';

/**
 * SideMenu
 *
 * Permissões:
 *  - COLABORADOR / FUNCIONARIO : OS, Clientes, Requerimentos, Protocolo, Relatórios
 *                                 SEM Saques e SEM Depósitos
 *  - GERENTE / ADMIN           : tudo acima + Saques, Depósitos, Área Fiscal, Ofícios/Memorandos,
 *                                 Usuários, Configurações
 *  - MANAGER                   : tudo
 *
 * Props:
 *   onOpenRequerimento – callback legado (mantido para compatibilidade com Home.jsx).
 *                        Se fornecido, ainda é chamado. Caso não seja fornecido,
 *                        o item "Requerimentos" abre /requerimentos em nova aba.
 */
function SideMenu({ onOpenRequerimento }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token === null || typeof token === 'undefined' || isExpired(token)) {
      window.location.href = '/login';
    } else {
      const decoded = decodeToken(token);
      setUserData(decoded);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => setIsExpanded(!isExpanded);

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    navigate('/login');
  };

  const closeOnMobile = () => {
    if (isMobile) setIsExpanded(false);
  };

  /**
   * Requerimentos:
   * - Se a prop onOpenRequerimento for fornecida (ex: Home.jsx), o clique normal usa o callback original.
   *   Ctrl/Shift/clique do meio e "Abrir em nova aba" (botão direito) abrem /requerimentos.
   * - Caso contrário (qualquer outra tela), abre /requerimentos em nova aba.
   *   Isso garante que o fluxo completo do requerimento funcione sem depender
   *   do userData disponível no componente pai.
   */
  const handleRequerimentos = (e) => {
    const abrindoEmNovaAba = e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1;
    if (typeof onOpenRequerimento === 'function' && !abrindoEmNovaAba) {
      // Comportamento legado — mantido para Home.jsx
      e.preventDefault();
      onOpenRequerimento();
    }
    closeOnMobile();
  };

  // Item do menu como link de verdade: permite "Abrir link em nova aba" no botão direito,
  // Ctrl+clique e clique do meio. Com newTab, o clique normal já abre em nova aba.
  const MenuLink = ({ to, newTab, onClick, children }) => (
    <li>
      <Link
        to={to}
        style={styles.menuItem}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
        onClick={onClick || closeOnMobile}
      >
        {children}
      </Link>
    </li>
  );

  // Normaliza role para maiúsculas e trata FUNCIONARIO como COLABORADOR
  const role = (userData?.tipoUsuario || '').toUpperCase();

  // Helpers de permissão
  const isColaborador = role === 'COLABORADOR' || role === 'FUNCIONARIO';
  const isGerente     = role === 'GERENTE' || role === 'ADMIN';
  const isManager     = role === 'MANAGER';

  // Acesso financeiro (Saques, Depósitos, Área Fiscal e Produtos):
  // GERENTE, ADMIN e MANAGER — NÃO o COLABORADOR
  const hasFinanceiro = isGerente || isManager;

  // ─── Ofícios/Memorandos: restrito a Manager e Gerente/Coordenador —
  // diferente de hasFinanceiro, aqui o ADMIN NÃO tem acesso ──────────────────
  const hasOficiosMemorandos = role === 'GERENTE' || isManager;

  // Acesso a gestão (Usuários, Configurações): GERENTE, ADMIN e MANAGER
  const hasGestao = isGerente || isManager;

  // Acesso exclusivo MANAGER (Empresas, Servidor)
  const hasManagerOnly = isManager;

  const menuStyle = isMobile
    ? isExpanded
      ? styles.menuExpandedMobile
      : styles.menuCollapsedMobile
    : isExpanded
    ? styles.menuExpanded
    : styles.menuCollapsed;

  return (
    <div style={menuStyle}>
      <button onClick={toggleMenu} style={styles.toggleButton}>
        {isExpanded ? '←' : <span style={styles.hamburgerIcon}>&#9776;</span>}
      </button>

      {isExpanded && userData && (
        <ul style={isMobile ? styles.menuListMobile : styles.menuList}>

          {/* HOME - todos */}
          <MenuLink to="/home">
            <FaHome style={styles.icon} />
            {isMobile ? null : 'Tela Inicial'}
          </MenuLink>

          {/* ORDEM DE SERVIÇO - todos */}
          <MenuLink to="/ordemServico">
            <FaClipboardList style={styles.icon} />
            {isMobile ? null : 'Ordem de Serviço'}
          </MenuLink>

          {/* CLIENTES - todos */}
          <MenuLink to="/Clientes">
            <FaUsers style={styles.icon} />
            {isMobile ? null : 'Clientes'}
          </MenuLink>

          {/* REQUERIMENTOS - todos (abre /requerimentos em nova aba ou modal via callback) */}
          <MenuLink
            to="/requerimentos"
            newTab={typeof onOpenRequerimento !== 'function'}
            onClick={handleRequerimentos}
          >
            <MdRequestPage style={styles.icon} />
            {isMobile ? null : (
              <span style={styles.itemComIcone}>
                Requerimentos
                {/* Ícone de "abre em nova aba" quando não há callback do pai */}
                {typeof onOpenRequerimento !== 'function' && (
                  <FaExternalLinkAlt style={styles.novaAbaIcon} title="Abre em nova aba" />
                )}
              </span>
            )}
          </MenuLink>

          {/* PROTOCOLO DE ATENDIMENTO - todos (abre em nova aba) */}
          <MenuLink to="/protocoloAtendimento" newTab>
            <FaFileAlt style={styles.icon} />
            {isMobile ? null : (
              <span style={styles.itemComIcone}>
                Protocolo de Atendimento
                <FaExternalLinkAlt style={styles.novaAbaIcon} title="Abre em nova aba" />
              </span>
            )}
          </MenuLink>

          {/* PLANILHAS DE GERENCIAMENTO - todos (abre em nova aba) */}
          <MenuLink to="/painel-crva" newTab>
            <FaTasks style={styles.icon} />
            {isMobile ? null : (
              <span style={styles.itemComIcone}>
                Planilhas de Gestão
                <FaExternalLinkAlt style={styles.novaAbaIcon} title="Abre em nova aba" />
              </span>
            )}
          </MenuLink>

          {/* RELATÓRIOS - todos */}
          <MenuLink to="/relatorios">
            <FaChartLine style={styles.icon} />
            {isMobile ? null : 'Relatórios'}
          </MenuLink>

          {/* AGENDA - todos (lembretes da empresa por data) */}
          <MenuLink to="/agenda">
            <FaCalendarAlt style={styles.icon} />
            {isMobile ? null : 'Agenda'}
          </MenuLink>

          {/* SAQUES - GERENTE, ADMIN, MANAGER (NÃO COLABORADOR) */}
          {hasFinanceiro && (
            <MenuLink to="/saques">
              <GiTakeMyMoney style={styles.icon} />
              {isMobile ? null : 'Saques'}
            </MenuLink>
          )}

          {/* DEPÓSITOS - GERENTE, ADMIN, MANAGER (NÃO COLABORADOR) */}
          {hasFinanceiro && (
            <MenuLink to="/depositos">
              <FaMoneyBillWave style={styles.icon} />
              {isMobile ? null : 'Depósitos'}
            </MenuLink>
          )}

          {/* ÁREA FISCAL - GERENTE, ADMIN, MANAGER (NÃO COLABORADOR) — abre em nova aba */}
          {hasFinanceiro && (
            <MenuLink to="/area-fiscal" newTab>
              <FaFileInvoiceDollar style={styles.icon} />
              {isMobile ? null : (
                <span style={styles.itemComIcone}>
                  Área Fiscal
                  <FaExternalLinkAlt style={styles.novaAbaIcon} title="Abre em nova aba" />
                </span>
              )}
            </MenuLink>
          )}

          {/* PRODUTOS - GERENTE, ADMIN, MANAGER (NÃO COLABORADOR) */}
          {hasFinanceiro && (
            <MenuLink to="/produtos">
              <FaBoxOpen style={styles.icon} />
              {isMobile ? null : 'Produtos'}
            </MenuLink>
          )}

          {/* OFÍCIOS/MEMORANDOS - apenas MANAGER e GERENTE/COORDENADOR (NÃO ADMIN, NÃO COLABORADOR) */}
          {hasOficiosMemorandos && (
            <MenuLink to="/oficios-memorandos">
              <FaStamp style={styles.icon} />
              {isMobile ? null : 'Ofícios/Memorandos'}
            </MenuLink>
          )}

          {/* DOCUMENTOS COMPLEMENTARES - todos (declarações e requerimentos de vistoria/DETRAN) */}
          <MenuLink to="/documentos-complementares">
            <FaFileSignature style={styles.icon} />
            {isMobile ? null : 'Documentos Complementares'}
          </MenuLink>

          {/* VEÍCULOS - todos (cadastro de veículos e importação de documentos em massa) */}
          <MenuLink to="/veiculos">
            <FaCar style={styles.icon} />
            {isMobile ? null : 'Veículos'}
          </MenuLink>

          {/* DIGITALIZAÇÃO - novo módulo (ponte + scanner) */}
          <MenuLink to="/digitalizacao">
            <FaPrint style={styles.icon} />
            {isMobile ? null : 'Digitalização'}
          </MenuLink>

          {/* USUÁRIOS - GERENTE, ADMIN, MANAGER */}
          {hasGestao && (
            <MenuLink to="/usuarios">
              <FaUserAlt style={styles.icon} />
              {isMobile ? null : 'Usuários'}
            </MenuLink>
          )}

          {/* CONFIGURAÇÕES - GERENTE, ADMIN, MANAGER */}
          {hasGestao && (
            <MenuLink to="/settings">
              <FaCog style={styles.icon} />
              {isMobile ? null : 'Configurações'}
            </MenuLink>
          )}

          {/* TROCAR EMPRESA - apenas MANAGER (escolhe com qual empresa trabalhar e passa a ver só os dados dela) */}
          {hasManagerOnly && (
            <MenuLink to="/selecionar-empresa">
              <FaExchangeAlt style={styles.icon} />
              {isMobile ? null : 'Trocar Empresa'}
            </MenuLink>
          )}

          {/* EMPRESAS - apenas MANAGER */}
          {hasManagerOnly && (
            <MenuLink to="/cadastroEmpresas">
              <IoMdBusiness style={styles.icon} />
              {isMobile ? null : 'Empresas'}
            </MenuLink>
          )}

          {/* SERVIDOR - apenas MANAGER */}
          {hasManagerOnly && (
            <MenuLink to="/server-info">
              <FaServer style={styles.icon} />
              {isMobile ? null : 'Servidor'}
            </MenuLink>
          )}

          {/* LOGOUT - todos */}
          <li style={{ ...styles.menuItem, ...styles.logoutItem }} onClick={handleLogout}>
            <FaSignOutAlt style={styles.icon} />
            {isMobile ? null : 'Logout'}
          </li>
        </ul>
      )}
    </div>
  );
}

const styles = {
  menuCollapsed: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '50px',
    height: '100%',
    backgroundColor: '#1a7a6e',
    color: '#fff',
    transition: 'width 0.3s',
    overflow: 'hidden',
    zIndex: 1000,
  },
  menuExpanded: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '220px',
    height: '100%',
    backgroundColor: '#1a7a6e',
    color: '#fff',
    transition: 'width 0.3s',
    zIndex: 1000,
    overflowY: 'auto',
  },
  menuCollapsedMobile: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: 'auto',
    backgroundColor: '#1a7a6e',
    color: '#fff',
    transition: 'height 0.3s',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    zIndex: 1000,
  },
  menuExpandedMobile: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: 'auto',
    backgroundColor: '#1a7a6e',
    color: '#fff',
    transition: 'height 0.3s',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    zIndex: 1000,
  },
  toggleButton: {
    width: 'auto',
    padding: '10px 14px',
    backgroundColor: '#155f55',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
  },
  hamburgerIcon: {
    fontSize: '22px',
  },
  menuList: {
    listStyleType: 'none',
    padding: '0',
    margin: '0',
  },
  menuListMobile: {
    listStyleType: 'none',
    padding: '0',
    margin: '0',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-around',
    overflowX: 'auto',
  },
  menuItem: {
    padding: '11px 16px',
    borderBottom: '1px solid #155f55',
    display: 'flex',
    alignItems: 'center',
    color: '#fff',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: '13.5px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  logoutItem: {
    marginTop: 'auto',
    borderTop: '2px solid #155f55',
  },
  icon: {
    marginRight: '10px',
    fontSize: '16px',
    minWidth: '16px',
    flexShrink: 0,
  },
  // Label com ícone de "abre em nova aba"
  itemComIcone: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
  },
  novaAbaIcon: {
    fontSize: '9px',
    opacity: 0.55,
    flexShrink: 0,
  },
};

export default SideMenu;
